import {
  Box,
  Button,
  Flex,
  Heading,
  Text,
  VStack,
  Wrap,
  WrapItem,
} from "@chakra-ui/react";
import { Container } from "@components/Container";
import Markdown from "@components/Markdown";
import { BenefitsProduct } from "@components/Products/BenefitsProduct/BenefitsProduct";
import { ArrowLeftSimpleIcon } from "@src/icons";
import Image from "next/image";
import Link from "next/link";
import { ProductButtons } from "../ProductButtons";
import styles from "./ProductDetailsHeader.module.scss";

const ProductDetailsHeaderBackButton = () => {
  return (
    <Link href="/produtos" passHref>
      <Button
        display={{ base: "none", xl: "flex" }}
        width={{ base: "auto", md: "9rem" }}
        padding={{ base: "0.5rem 1rem", md: "1.5rem 2rem" }}
        backgroundColor={"white"}
        color="primary.500"
        variant="solid"
        justifyContent={"space-evenly"}
        alignItems={"center"}
        fontWeight="600"
        fontSize="1.2rem"
        leftIcon={<ArrowLeftSimpleIcon color="primary.500" />}
      >
        Voltar
      </Button>
    </Link>
  );
};

export interface ProductDetailsHeaderAdvantage {
  id: number;
  icon: string;
  description: string;
  title: string;
}

export interface ProductDetailsHeaderProps {
  title?: string;
  description?: string;
  code?: string;
  baseCode?: string;
  category?: string;
  highLightImage?: string;
  purchaseLink?: string;
  advantages?: ProductDetailsHeaderAdvantage[];
}

export const ProductDetailsHeader = (props: ProductDetailsHeaderProps) => {
  return (
    <Container
      justifyContent={"flex-start"}
      marginTop={{ base: "1rem", md: "3rem" }}
      paddingX={{ base: "1.8125rem", xl: 0 }}
    >
      <VStack
        width={"full"}
        gridGap={{ base: "1rem", md: "3rem" }}
        alignItems="flex-start"
      >
        <ProductDetailsHeaderBackButton />
        <Flex
          flexDirection={{ base: "column", xl: "row" }}
          flex={1}
          width="full"
          justify={"space-between"}
        >
          <Flex
            position={"relative"}
            flex={1}
            flexDirection={"column"}
            gridGap={{ base: "1rem", xl: "3.375rem" }}
          >
            <Flex flexDirection={"column"}>
              {props.category && (
                <Text
                  fontSize={{ base: "1rem", md: "1.625rem" }}
                  fontWeight="400"
                  color="gray.500"
                >
                  {props.category}
                </Text>
              )}
              <Heading
                fontWeight={"600"}
                fontSize={{ base: "3rem", md: "4.0625rem" }}
              >
                {props.title}
              </Heading>
            </Flex>
            <Flex
              flex={{ base: 1, xl: undefined }}
              justify={{ base: "center", xl: undefined }}
            >
              <Flex
                flexDirection={"column"}
                position={{ base: "initial", xl: "absolute" }}
                width={{ base: "680px", md: "680px" }}
                left={{ base: "initial", xl: "0px" }}
                bottom={"-44px"}
              >
                {props.highLightImage && (
                  <Image
                    src={props.highLightImage}
                    width={680}
                    height={404}
                    quality={100}
                    alt="Imagem"
                  />
                )}
                {(props.code || props.baseCode) && (
                  <Box
                    position={{ base: "initial", md: "relative" }}
                    bottom={"44px"}
                    fontSize={{ base: "0.8rem", md: "1rem" }}
                    marginY={{ base: "1.5rem", md: 0 }}
                  >
                    {props.code && (
                      <Text color="gray.600">Código: {props.code}</Text>
                    )}
                    {props.baseCode && (
                      <Text color="gray.600">
                        Código base: {props.baseCode}
                      </Text>
                    )}
                  </Box>
                )}
              </Flex>
            </Flex>
          </Flex>
          <Flex
            flex={1}
            flexDirection={"column"}
            gridGap={{ base: "1rem", md: "2rem", lg: "3.375rem" }}
            maxWidth={{ base: "100%", xl: "869px" }}
            minWidth={{ base: "full", xl: "869px" }}
          >
            {(props.description || props.advantages?.length) && (
              <Flex
                flexDirection={"column"}
                gridGap={"2rem"}
                backgroundColor="gray.50"
                borderRadius={{ base: "1rem", xl: "none" }}
                borderTopLeftRadius={{ base: "1rem", xl: "3.75rem" }}
                borderBottomRightRadius={{ base: "1rem", xl: "3.75rem" }}
                padding={{
                  base: "1.5rem",
                  lg: "3rem",
                  xl: "3.5rem 4rem 4rem 11.75rem",
                }}
                minHeight={{ base: "auto", xl: "27.1875rem" }}
              >
                {props.description && (
                  <Box className={styles.description}>
                    <Markdown>{props.description}</Markdown>
                  </Box>
                )}
                {props.advantages?.length && (
                  <Wrap spacing={"1rem"} justify="center">
                    {props.advantages.map((advantage) => (
                      <WrapItem key={advantage.id}>
                        <BenefitsProduct {...advantage} size="large" />
                      </WrapItem>
                    ))}
                  </Wrap>
                )}
              </Flex>
            )}
            <Flex
              justifyContent={"center"}
              paddingLeft={{ base: 0, xl: "5rem" }}
            >
              <ProductButtons purchaseLink={props.purchaseLink} />
            </Flex>
          </Flex>
        </Flex>
      </VStack>
    </Container>
  );
};
